import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  History, X, Music2, Clock, Copy, Check, ChevronDown, ChevronUp
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnalysisHistory = ({ isOpen, onClose, userEmail, onLoadAnalysis }) => {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (isOpen && userEmail) {
      fetchHistory();
    }
  }, [isOpen, userEmail]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analysis-history/${userEmail}`);
      setAnalyses(response.data.analyses || []);
    } catch (error) {
      console.error("Failed to fetch history:", error);
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown date';
    }
  };

  const copyPrompt = (prompt, id) => {
    navigator.clipboard.writeText(prompt);
    setCopiedId(id);
    toast.success("Prompt copied!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="modal-overlay" onClick={onClose} data-testid="history-overlay">
        <motion.div 
          className="history-modal"
          onClick={e => e.stopPropagation()}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          data-testid="analysis-history"
          style={{
            background: 'linear-gradient(135deg, rgba(20,20,30,0.98) 0%, rgba(10,10,20,0.98) 100%)',
            borderRadius: '20px',
            padding: '0',
            maxWidth: '600px',
            width: '95%',
            maxHeight: '85vh',
            overflow: 'hidden',
            border: '1px solid rgba(204,255,0,0.2)',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.8)'
          }}
        >
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(147,112,219,0.2) 0%, rgba(204,255,0,0.1) 100%)',
            padding: '20px 24px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <History className="w-6 h-6" style={{ color: '#9370db' }} />
              <div>
                <h2 style={{ color: '#fff', fontSize: '1.2rem', fontWeight: 700, margin: 0 }}>
                  Analysis History
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', margin: 0 }}>
                  {analyses.length} past analyses
                </p>
              </div>
            </div>
            <button 
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#fff'
              }}
              data-testid="history-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div style={{ padding: '16px', overflowY: 'auto', maxHeight: 'calc(85vh - 100px)' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
                <div className="spinner" style={{ margin: '0 auto 16px' }} />
                Loading history...
              </div>
            ) : analyses.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
                <Music2 className="w-12 h-12" style={{ margin: '0 auto 16px', opacity: 0.3 }} />
                <p>No analyses yet</p>
                <p style={{ fontSize: '0.85rem' }}>Upload audio or describe a style to get started!</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {analyses.map((analysis, index) => (
                  <div 
                    key={analysis.id || index}
                    style={{
                      background: 'rgba(0,0,0,0.3)',
                      border: '1px solid rgba(147,112,219,0.2)',
                      borderRadius: '12px',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Analysis Header */}
                    <div 
                      onClick={() => setExpandedId(expandedId === analysis.id ? null : analysis.id)}
                      style={{
                        padding: '14px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '10px',
                          marginBottom: '4px'
                        }}>
                          <Music2 className="w-4 h-4" style={{ color: '#ccff00' }} />
                          <span style={{ 
                            color: '#fff', 
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            maxWidth: '300px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {analysis.filename || 'Style Description'}
                          </span>
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '12px',
                          color: 'rgba(255,255,255,0.5)',
                          fontSize: '0.8rem'
                        }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Clock className="w-3 h-3" />
                            {formatDate(analysis.timestamp)}
                          </span>
                          {analysis.audio_dna && (
                            <>
                              <span style={{ color: '#ff0066' }}>
                                {analysis.audio_dna.bpm?.toFixed(0)} BPM
                              </span>
                              <span style={{ color: '#00bfff' }}>
                                {analysis.audio_dna.key} {analysis.audio_dna.mode}
                              </span>
                            </>
                          )}
                          <span style={{ color: '#ccff00' }}>
                            {analysis.total_prompts} prompts
                          </span>
                        </div>
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.5)' }}>
                        {expandedId === analysis.id ? 
                          <ChevronUp className="w-5 h-5" /> : 
                          <ChevronDown className="w-5 h-5" />
                        }
                      </div>
                    </div>

                    {/* Expanded Prompts */}
                    {expandedId === analysis.id && analysis.prompts && (
                      <div style={{
                        borderTop: '1px solid rgba(255,255,255,0.1)',
                        padding: '12px 16px',
                        background: 'rgba(0,0,0,0.2)'
                      }}>
                        <div style={{ 
                          fontSize: '0.75rem', 
                          color: 'rgba(255,255,255,0.4)',
                          marginBottom: '10px'
                        }}>
                          Click any prompt to copy
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: '8px',
                          maxHeight: '200px',
                          overflowY: 'auto'
                        }}>
                          {analysis.prompts.slice(0, 10).map((prompt, pIndex) => (
                            <div 
                              key={pIndex}
                              onClick={() => copyPrompt(prompt.prompt, `${analysis.id}-${pIndex}`)}
                              style={{
                                padding: '10px 12px',
                                background: 'rgba(147,112,219,0.1)',
                                border: '1px solid rgba(147,112,219,0.2)',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '10px',
                                transition: 'all 0.2s'
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <div style={{ 
                                  color: '#fff', 
                                  fontSize: '0.85rem',
                                  lineHeight: 1.4
                                }}>
                                  {prompt.prompt}
                                </div>
                                <div style={{ 
                                  display: 'flex', 
                                  gap: '6px', 
                                  marginTop: '6px',
                                  flexWrap: 'wrap'
                                }}>
                                  {prompt.platforms?.slice(0, 3).map((p, i) => (
                                    <span key={i} style={{
                                      padding: '2px 6px',
                                      background: 'rgba(204,255,0,0.1)',
                                      borderRadius: '4px',
                                      fontSize: '0.65rem',
                                      color: '#ccff00'
                                    }}>
                                      {p}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <div style={{ color: copiedId === `${analysis.id}-${pIndex}` ? '#22c55e' : 'rgba(255,255,255,0.4)' }}>
                                {copiedId === `${analysis.id}-${pIndex}` ? 
                                  <Check className="w-4 h-4" /> : 
                                  <Copy className="w-4 h-4" />
                                }
                              </div>
                            </div>
                          ))}
                          {analysis.prompts.length > 10 && (
                            <div style={{ 
                              textAlign: 'center', 
                              color: 'rgba(255,255,255,0.4)',
                              fontSize: '0.8rem',
                              padding: '8px'
                            }}>
                              +{analysis.prompts.length - 10} more prompts
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AnalysisHistory;
